import { useState, useEffect } from 'react';
import dashboardService from '../services/dashboardService';

/**
 * Custom hook for managing comparison data
 * Fetches comparison data based on selected mode
 * 
 * @param {Object} currentDateRange - Current date range
 * @param {string} comparisonMode - Comparison mode ('lastMonth', 'lastYear', 'budget')
 * @returns {Object} { comparisonData, isLoading, error }
 */
export const useComparisonData = (currentDateRange, comparisonMode) => {
  const [comparisonData, setComparisonData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (comparisonMode === 'none' || !currentDateRange) {
      setComparisonData(null);
      return;
    }

    const fetchComparisonData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let comparisonDateRange;

        switch (comparisonMode) {
          case 'lastMonth': {
            // Get the previous period of the same length
            const daysDiff = Math.ceil((currentDateRange.endDate - currentDateRange.startDate) / (1000 * 60 * 60 * 24));
            const comparisonStart = new Date(currentDateRange.startDate);
            comparisonStart.setDate(comparisonStart.getDate() - daysDiff);
            const comparisonEnd = new Date(currentDateRange.startDate);
            comparisonEnd.setDate(comparisonEnd.getDate() - 1);
            
            comparisonDateRange = {
              startDate: comparisonStart,
              endDate: comparisonEnd
            };
            break;
          }

          case 'lastYear': {
            // Get the same period last year
            const comparisonStart = new Date(currentDateRange.startDate);
            comparisonStart.setFullYear(comparisonStart.getFullYear() - 1);
            const comparisonEnd = new Date(currentDateRange.endDate);
            comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1);
            
            comparisonDateRange = {
              startDate: comparisonStart,
              endDate: comparisonEnd
            };
            break;
          }

          case 'budget':
            // For budget comparison, we'll use the current date range
            // and compare against budget data (to be implemented)
            comparisonDateRange = currentDateRange;
            break;

          default:
            comparisonDateRange = null;
        }

        if (comparisonDateRange) {
          console.log('📊 Fetching comparison data:', { mode: comparisonMode, dateRange: comparisonDateRange });
          const data = await dashboardService.getDashboardData(null, comparisonDateRange);
          setComparisonData(data);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching comparison data:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchComparisonData();
  }, [currentDateRange, comparisonMode]);

  // Calculate comparison metrics
  const getComparisonMetrics = (currentValue, comparisonValue) => {
    if (!comparisonValue || comparisonValue === 0) {
      return { change: 0, percentChange: 0, isIncrease: false };
    }

    const change = currentValue - comparisonValue;
    const percentChange = (change / Math.abs(comparisonValue)) * 100;
    const isIncrease = change > 0;

    return {
      change: Math.round(change * 100) / 100,
      percentChange: Math.round(percentChange * 100) / 100,
      isIncrease
    };
  };

  return {
    comparisonData,
    isLoading,
    error,
    getComparisonMetrics
  };
};

export default useComparisonData;

